import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Spinner,
} from '@monksflow/monks-ui';
import { EllipsisVerticalIcon } from 'lucide-react';
import type { ProjectPerformance } from '@/entities/media-campaign';
import { useReportExport, type ReportFormat } from '../model/useReportExport';
import { scopeLabelFrom } from '../lib/scope-label';

/** Held as keys, not labels: a module-scope `t(...)` would freeze at import. */
const FORMAT_ITEMS = [
  { format: 'pptx', labelKey: 'performanceReports.reportMenu.downloadReportPpt' },
  { format: 'xlsx', labelKey: 'performanceReports.reportMenu.downloadReportXlsx' },
] as const satisfies ReadonlyArray<{ format: ReportFormat; labelKey: string }>;

export interface ReportMenuProps {
  projectId: string;
  projectName: string;
  /** The response on screen. Undefined while it loads, which disables the menu. */
  performance: ProjectPerformance | undefined;
  window: { startDate: string; endDate: string };
  /** The platform filter in force, so the report's heading names what it was narrowed to. */
  platforms: ReadonlyArray<string>;
  /** The campaign the view is scoped to, resolved to a name by the caller. */
  campaignScope?: string;
}

/**
 * Report downloads, from the subheader rather than a view of their own.
 *
 * A report is an action on whatever the dashboard is currently showing, not a place to
 * visit, so it lives beside the filters that decide what it will contain.
 *
 * The download starts on its own once the export completes. The link stays in the DOM as
 * a fallback for a browser that blocks the programmatic click, so a blocked popup costs
 * the user a second click rather than the whole report.
 */
export function ReportMenu({
  projectId,
  projectName,
  performance,
  window: reportWindow,
  platforms,
  campaignScope,
}: ReportMenuProps) {
  const { t } = useTranslation('projects');
  const { generate, status, downloadUrl, isGenerating, error } = useReportExport(projectId);
  const downloadRef = useRef<HTMLAnchorElement>(null);
  const startedFor = useRef<string | null>(null);

  useEffect(() => {
    // Guarded on the URL rather than on status alone: this effect re-runs on every render
    // while the status stays 'completed', and without the guard a later unrelated render
    // would download the same file again.
    if (status !== 'completed' || !downloadUrl || startedFor.current === downloadUrl) return;
    startedFor.current = downloadUrl;
    downloadRef.current?.click();
  }, [status, downloadUrl]);

  return (
    <div className="flex items-center gap-2">
      {isGenerating && (
        <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Spinner className="size-3.5" />
          {t('performanceReports.reportMenu.preparing')}
        </span>
      )}

      {status === 'failed' && (
        <span className="text-sm text-destructive" role="alert">
          {t('performanceReports.reportMenu.reportFailed')}
        </span>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('performanceReports.reportMenu.reportDownloads')}
            disabled={!performance}
          >
            <EllipsisVerticalIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {FORMAT_ITEMS.map(({ format, labelKey }) => (
            <DropdownMenuItem
              key={format}
              disabled={!performance || isGenerating}
              onSelect={() => {
                if (!performance) return;
                void generate({
                  format,
                  performance,
                  projectName,
                  window: reportWindow,
                  scopeLabel: scopeLabelFrom({ platforms, campaignScope }),
                });
              }}
            >
              {t(labelKey)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Rendered but not shown: the effect above clicks it, and it remains reachable if
          the browser refused that click. */}
      <a
        ref={downloadRef}
        href={downloadUrl ?? undefined}
        download
        hidden={!downloadUrl}
        className="text-sm underline"
        data-testid="report-download-link"
      >
        {t('performanceReports.reportMenu.downloadReady')}
      </a>

      {error && (
        <span className="sr-only" role="status">
          {error.message}
        </span>
      )}
    </div>
  );
}
